import React, { useState, useCallback } from 'react';
import { Sparkles, Shield, Users, Star, Zap, Laptop, Mic, Palette, Video, Globe, Bot, Trophy } from 'lucide-react';
import VSLOptInForm from '@/components/VSLOptInForm';
import VSLVideoPlayer from '@/components/VSLVideoPlayer';
import AdmissionForm from '@/components/AdmissionForm';
import Footer from '@/components/Footer';
import videoThumbnail from '@/assets/video-thumbnail.png';
interface LeadData {
  id: string;
  name: string;
  email: string;
  phone: string;
}

const Index = () => {
  const [leadData, setLeadData] = useState<LeadData | null>(null);
  const [showApplication, setShowApplication] = useState(false);
  const [showOptInForm, setShowOptInForm] = useState(false);

  const handleOptInSuccess = useCallback((data: LeadData) => {
    setLeadData(data);
    setShowOptInForm(false);
  }, []);

  const handleVideoComplete = useCallback(() => {
    setShowApplication(true);
  }, []);

  const handleWatchClick = useCallback(() => {
    setShowOptInForm(true);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-900 via-blue-900 to-slate-900">
      {/* Hero Section */}
      <div className="container mx-auto px-4 py-12 md:py-20">
        <div className="text-center max-w-4xl mx-auto mb-12">
          {/* Target Badge */}
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-orange-500/20 to-red-500/20 border border-orange-400/30 text-orange-300 px-5 py-2 rounded-full text-sm font-bold mb-8">
            <Zap className="h-4 w-4 fill-orange-400 text-orange-400" />
            <span>🔥 For Parents of Students in Grades 6–12</span>
          </div>

          {/* Headline */}
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6 leading-tight">
            How Your Child Can Use AI to Study Smarter, Score Higher &
            <br />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 via-orange-400 to-pink-400">
              Build Future-Ready Skills — in Just 8 Weeks.
            </span>
          </h1>

          {/* Subheadline - Pain Point */}
          <p className="text-lg md:text-xl text-gray-400 mb-10 max-w-3xl mx-auto leading-relaxed italic">
            (While other students are still struggling with homework stress, memorizing without understanding, and outdated learning methods.)
          </p>
        </div>

        {/* Main Content Area - Video Section */}
        <div className="max-w-4xl mx-auto mb-16">
          {!leadData ? (
            // Step 1: Video Thumbnail or Opt-in Form
            <div className="flex flex-col items-center">
              {!showOptInForm ? (
                // Show video thumbnail with play button
                <button 
                  onClick={handleWatchClick}
                  className="relative w-full aspect-video bg-gray-800 rounded-2xl overflow-hidden group cursor-pointer transition-transform hover:scale-[1.02]"
                >
                  <img 
                    src={videoThumbnail} 
                    alt="Transform Your Child's Learning with AI" 
                    className="absolute inset-0 w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/30 flex items-center justify-center group-hover:bg-black/20 transition-colors">
                    <div className="w-20 h-20 md:w-24 md:h-24 bg-red-600 rounded-full flex items-center justify-center shadow-2xl group-hover:bg-red-500 transition-all group-hover:scale-110">
                      <div className="w-0 h-0 border-l-[24px] md:border-l-[28px] border-l-white border-t-[15px] md:border-t-[18px] border-t-transparent border-b-[15px] md:border-b-[18px] border-b-transparent ml-2"></div>
                    </div>
                  </div>
                </button>
              ) : (
                // Show opt-in form after clicking play
                <div className="w-full animate-in fade-in slide-in-from-bottom-4 duration-500">
                  <div className="text-center mb-6">
                    <p className="text-white text-xl font-semibold mb-2">🎬 Almost there!</p>
                    <p className="text-blue-200">Enter your details to watch the free presentation</p>
                  </div>
                  <VSLOptInForm onSuccess={handleOptInSuccess} />
                </div>
              )}
            </div>
          ) : (
            // Step 2: Video Player & Application Form
            <div className="space-y-16">
              <VSLVideoPlayer 
                leadId={leadData.id} 
                onVideoComplete={handleVideoComplete} 
              />

              {showApplication && (
                <div className="animate-in fade-in slide-in-from-bottom-8 duration-700">
                  <div className="text-center mb-8">
                    <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
                      Apply for the AI SuperCreators Program™
                    </h2>
                    <p className="text-xl text-blue-200">
                      8 Weeks | Live Online | Limited Seats
                    </p>
                  </div>
                  <AdmissionForm leadData={leadData} />
                </div>
              )}
            </div>
          )}
        </div>

        {/* What Students Learn Section */}
        <div className="max-w-4xl mx-auto mb-16">
          <div className="text-center mb-10">
            <p className="text-blue-400 text-sm font-medium uppercase tracking-wider mb-3">📌 Inside this program</p>
            <h2 className="text-2xl md:text-3xl font-bold text-white">
              Students Learn:
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-cyan-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Bot className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold mb-1">🧠 How to use AI as a study assistant</p>
                <p className="text-gray-400 text-sm">Get personalized help with any subject, anytime</p>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-teal-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Zap className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold mb-1">📝 How to complete homework faster</p>
                <p className="text-gray-400 text-sm">With better clarity and understanding</p>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Mic className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold mb-1">🎤 How to create confident presentations</p>
                <p className="text-gray-400 text-sm">Build impressive school projects with AI</p>
              </div>
            </div>

            <div className="bg-white/5 backdrop-blur-sm rounded-xl p-5 border border-white/10 flex items-start gap-4">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-500 to-red-500 rounded-lg flex items-center justify-center flex-shrink-0">
                <Sparkles className="w-6 h-6 text-white" />
              </div>
              <div>
                <p className="text-white font-semibold mb-1">🚀 How to develop future skills</p>
                <p className="text-gray-400 text-sm">Creativity, critical thinking & problem-solving</p>
              </div>
            </div>
          </div>
        </div>

        {/* No Pressure Section */}
        <div className="max-w-3xl mx-auto mb-16 text-center">
          <div className="bg-gradient-to-r from-green-500/10 to-teal-500/10 border border-green-400/20 rounded-xl p-8">
            <p className="text-2xl md:text-3xl font-bold text-white mb-4">
              💡 No Coding. No Long Classes. No Pressure.
            </p>
            <p className="text-xl text-green-300">
              Just smart learning with AI — step by step.
            </p>
          </div>
        </div>

        {/* Trust Badges */}
        <div className="flex flex-wrap items-center justify-center gap-4 md:gap-8 text-gray-400 text-sm mb-12">
          <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
            <Shield className="h-4 w-4 text-green-400" />
            <span>Safe, Child-Friendly AI Tools</span>
          </div>
          <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
            <Users className="h-4 w-4 text-blue-400" />
            <span>Small Batch Learning</span>
          </div>
          <div className="flex items-center gap-2 bg-white/5 px-4 py-2 rounded-full">
            <Trophy className="h-4 w-4 text-yellow-400" />
            <span>10+ Years Teaching Experience</span>
          </div>
        </div>

        {/* Social Proof */}
        <div className="text-center">
          <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 max-w-3xl mx-auto border border-white/10">
            <p className="text-xl md:text-2xl text-white font-medium mb-6">
              ✨ AI SuperCreators Program™ — 8 Weeks
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4 text-sm text-gray-400">
              <span className="flex items-center gap-1">
                <span className="text-green-400">✓</span> Live Online Sessions
              </span>
              <span className="flex items-center gap-1">
                <span className="text-green-400">✓</span> Hands-on Projects
              </span>
              <span className="flex items-center gap-1">
                <span className="text-green-400">✓</span> AI Tools Access
              </span>
              <span className="flex items-center gap-1">
                <span className="text-green-400">✓</span> Certification
              </span>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default Index;
